import {
  ActionIcon,
  AppShell,
  Badge,
  Box,
  Burger,
  Group,
  Menu,
  NavLink,
  Stack,
  Text,
  Title,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBuilding,
  IconBuildingCommunity,
  IconChartBar,
  IconCheckupList,
  IconDashboard,
  IconFileExport,
  IconLanguage,
  IconLogout,
  IconMoon,
  IconSettings,
  IconSun,
  IconUser,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { useCanWrite, useIsAdmin } from '@/auth/useRequireRole';
import { appConfig } from '@/auth/authConfig';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n/i18n';
import { MandantSwitcher } from './MandantSwitcher';
import { EinrichtungSwitcher } from './EinrichtungSwitcher';
import { JahrSwitcher } from './JahrSwitcher';

interface NavItem {
  to: string;
  labelKey: string;
  icon: typeof IconDashboard;
  adminOnly?: boolean;
  /** When true, only users with at least one of the canWrite roles see the entry. */
  writerOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', labelKey: 'nav.cockpit', icon: IconDashboard },
  { to: '/admin/traeger', labelKey: 'nav.traeger', icon: IconBuildingCommunity, adminOnly: true },
  { to: '/einrichtungen', labelKey: 'nav.einrichtungen', icon: IconBuilding, writerOnly: true },
  { to: '/plaene', labelKey: 'nav.plaene', icon: IconCheckupList },
  { to: '/vergleich', labelKey: 'nav.vergleich', icon: IconChartBar },
  { to: '/export', labelKey: 'nav.export', icon: IconFileExport },
  { to: '/admin', labelKey: 'nav.admin', icon: IconSettings, adminOnly: true },
];

export function Layout() {
  const { t, i18n } = useTranslation('common');
  const [opened, { toggle }] = useDisclosure();
  const { user, logout } = useAuth();
  const isAdmin = useIsAdmin();
  const canWrite = useCanWrite();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const location = useLocation();

  const visibleNav = NAV_ITEMS.filter((n) => {
    if (n.adminOnly && !isAdmin) return false;
    if (n.writerOnly && !canWrite) return false;
    return true;
  });

  return (
    <AppShell
      header={{ height: 80 }}
      navbar={{ width: 260, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header
        style={{
          borderBottom: '1px solid var(--mantine-color-gray-2)',
          background: 'var(--mantine-color-body)',
        }}
      >
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="md" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Link to="/" aria-label="Startseite" style={{ display: 'flex' }}>
              <img
                src="/brand/lag-logo.jpg"
                alt="LAG Hessen — Freie Kitaträger"
                style={{ height: 56, width: 'auto', display: 'block' }}
              />
            </Link>
            <Box
              style={{
                width: 1,
                height: 40,
                background: 'var(--mantine-color-gray-3)',
              }}
            />
            <Stack gap={0}>
              <Title order={4} c="#072c5e" style={{ lineHeight: 1.1 }}>
                LFT
              </Title>
              <Text size="xs" c="dimmed">
                Wirtschaftsplanung
              </Text>
            </Stack>
            <Badge color={appConfig.appEnv === 'prod' ? 'red' : 'orange'} variant="light">
              {appConfig.appEnv === 'prod' ? t('app.envBadge.prod') : t('app.envBadge.dev')}
            </Badge>
          </Group>
          <Group gap="sm" wrap="nowrap">
            <MandantSwitcher />
            <EinrichtungSwitcher />
            <JahrSwitcher />
            <ActionIcon
              variant="subtle"
              onClick={() => toggleColorScheme()}
              aria-label={
                colorScheme === 'dark' ? t('header.theme.light') : t('header.theme.dark')
              }
            >
              {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
            </ActionIcon>
            <Menu position="bottom-end" withArrow>
              <Menu.Target>
                <ActionIcon variant="subtle" aria-label={t('header.user')}>
                  <IconUser size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>
                  <Text size="xs">
                    {user?.profile?.preferred_username ?? user?.profile?.email ?? '—'}
                  </Text>
                </Menu.Label>
                <Menu.Divider />
                <Menu.Label>
                  <Group gap={4}>
                    <IconLanguage size={14} /> {t('header.language')}
                  </Group>
                </Menu.Label>
                {SUPPORTED_LANGUAGES.map((lng) => (
                  <Menu.Item
                    key={lng}
                    onClick={() => void i18n.changeLanguage(lng)}
                    rightSection={i18n.language === lng ? '✓' : null}
                  >
                    {lng.toUpperCase()}
                  </Menu.Item>
                ))}
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={14} />}
                  onClick={() => void logout()}
                >
                  {t('nav.logout')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm">
        <Stack gap={4}>
          {visibleNav.map((n) => {
            const Icon = n.icon;
            const active =
              n.to === '/' ? location.pathname === '/' : location.pathname.startsWith(n.to);
            return (
              <NavLink
                key={n.to}
                component={Link}
                to={n.to}
                label={t(n.labelKey)}
                leftSection={<Icon size={18} />}
                active={active}
              />
            );
          })}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}

// Mark export as used so unused-locals lint stays clean
export type { SupportedLanguage };
